// =====================================================================
// Cross-tab presence bookkeeping for the duplicate-tab banner. Pure, so the rules are
// testable in the node test env — the hook that owns the channel and the timers is in
// useOpenTabCount.ts.
//
// Tabs announce themselves and answer each other's pings; a peer that stays silent through
// our pings is treated as gone. Every ambiguous case counts a peer as PRESENT: over-counting
// leaves a stale banner up, which is mildly annoying, while under-counting hides a live
// duplicate, which is the thing the banner exists to surface.
// =====================================================================

// Three missed pings' worth of grace at the normal cadence. Not a wall-clock deadline — see
// the caller, which stretches it when its own timer has been throttled.
export const TAB_TTL_MS = 12_000;

export type TabMessage =
  | {
      kind: 'here';
      tabId: string;
      identity: string;
      at: number; // epoch ms, stamped by the sender
      wantReply: boolean;
    }
  | { kind: 'bye'; tabId: string; identity: string };

// One gate for everything arriving off the wire, so the shape is checked once instead of
// being re-derived by each rule. A tab running an older bundle can put anything on this
// channel, and a message we cannot read must not become a phantom peer.
export function parseTabMessage(msg: unknown): TabMessage | null {
  if (typeof msg !== 'object' || msg === null) return null;
  const m = msg as Record<string, unknown>;
  if (typeof m.tabId !== 'string' || typeof m.identity !== 'string') return null;
  if (m.kind === 'bye') return { kind: 'bye', tabId: m.tabId, identity: m.identity };
  if (m.kind !== 'here' || typeof m.at !== 'number' || !Number.isFinite(m.at)) return null;
  return {
    kind: 'here',
    tabId: m.tabId,
    identity: m.identity,
    at: m.at,
    wantReply: m.wantReply === true,
  };
}

// Peers by tab id → when they last spoke. Returns the SAME map whenever the set of peers is
// unchanged — including the common case, a repeat announcement from a peer we already know
// — so the caller can skip the re-render.
export function applyTabMessage(
  peers: Map<string, number>,
  msg: TabMessage,
  self: { tabId: string; identity: string },
): Map<string, number> {
  // Another SDR's tabs are not our problem: dev impersonation (`?sdr=`) opens tabs as
  // different people on purpose.
  if (msg.identity !== self.identity || msg.tabId === self.tabId) return peers;

  if (msg.kind === 'bye') {
    if (!peers.has(msg.tabId)) return peers;
    const next = new Map(peers);
    next.delete(msg.tabId);
    return next;
  }
  if (peers.has(msg.tabId)) {
    peers.set(msg.tabId, msg.at); // refresh only — same peers, so keep the reference
    return peers;
  }
  const next = new Map(peers);
  next.set(msg.tabId, msg.at);
  return next;
}

// Drops peers that have said nothing for `ttlMs` — a tab killed without firing pagehide
// (crash, force quit, machine sleep). Same-reference contract as applyTabMessage.
export function prunePeers(
  peers: Map<string, number>,
  now: number,
  ttlMs: number = TAB_TTL_MS,
): Map<string, number> {
  const next = new Map<string, number>();
  for (const [tabId, at] of peers) if (now - at <= ttlMs) next.set(tabId, at);
  return next.size === peers.size ? peers : next;
}

// Should this tab answer `msg`? Only a peer's explicit ask, and an answer never asks back,
// or two tabs reply to each other forever.
export function shouldReply(msg: TabMessage, self: { tabId: string; identity: string }): boolean {
  if (msg.kind !== 'here') return false;
  return msg.wantReply && msg.identity === self.identity && msg.tabId !== self.tabId;
}
